import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Star } from "lucide-react";
import { ORDER_URL } from "../data/menu";
import { IMAGES } from "../data/site";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yDish = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center overflow-hidden pt-28 md:pt-32 bg-brand-cream grain-overlay"
    >
      {/* decorative shapes */}
      <div className="absolute top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-brand-red/5 blur-3xl" />
      <div className="absolute bottom-20 -right-32 w-[34rem] h-[34rem] rounded-full bg-brand-mustard/10 blur-3xl" />

      {/* Floating ingredients */}
      <motion.img
        src={IMAGES.chili}
        alt=""
        aria-hidden="true"
        className="hidden md:block absolute top-32 left-[8%] w-20 h-20 object-cover rounded-full shadow-xl ring-4 ring-brand-cream"
        animate={{ y: [0, -22, 0], rotate: [0, 10, -5, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.img
        src={IMAGES.mint}
        alt=""
        aria-hidden="true"
        className="hidden md:block absolute top-1/3 right-[6%] w-24 h-24 object-cover rounded-full shadow-xl ring-4 ring-brand-cream"
        animate={{ y: [0, -16, 0], rotate: [0, -8, 4, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />
      <motion.img
        src={IMAGES.spice1}
        alt=""
        aria-hidden="true"
        className="hidden lg:block absolute bottom-32 left-[12%] w-16 h-16 object-cover rounded-full shadow-xl ring-4 ring-brand-cream"
        animate={{ y: [0, -14, 0], rotate: [0, 14, 0] }}
        transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
      />
      <motion.img
        src={IMAGES.lemon}
        alt=""
        aria-hidden="true"
        className="hidden lg:block absolute bottom-40 right-[14%] w-20 h-20 object-cover rounded-full shadow-xl ring-4 ring-brand-cream"
        animate={{ y: [0, -20, 0], rotate: [0, -10, 5, 0] }}
        transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center w-full">
        {/* Left: text */}
        <motion.div style={{ y: yText, opacity }} className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-red/10 border border-brand-red/20 mb-6"
          >
            <Star size={12} className="text-brand-mustard fill-brand-mustard" />
            <span className="font-heading uppercase text-[11px] tracking-[0.25em] font-bold text-brand-red">
              Pittsburgh's Indian Street Kitchen · Since 2014
            </span>
          </motion.div>

          <h1
            className="font-display uppercase text-[3.5rem] sm:text-[5rem] md:text-[7rem] lg:text-[8.5rem] leading-[0.85] tracking-tight text-brand-charcoal"
            style={{ WebkitTextStroke: "1.5px currentColor", fontWeight: 900 }}
          >
            <motion.span
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="block"
            >
              Taste the
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="block text-brand-red italic"
              style={{ fontStyle: "italic" }}
            >
              Difference.
            </motion.span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-7 max-w-xl text-base md:text-lg text-brand-charcoal/70 leading-relaxed text-balance"
          >
            Authentic Indian street flavours, slow-cooked the way grandma did it.
            Tandoor smoke, dum biryani, hand-pounded masalas — crafted fresh by a family
            who's been feeding Pittsburgh for over a decade.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href={ORDER_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-order-cta"
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-brand-red text-brand-cream font-heading uppercase text-xs tracking-[0.2em] font-bold hover:bg-brand-deepred shadow-[0_14px_40px_-10px_rgba(179,0,27,0.55)] hover:shadow-[0_20px_50px_-10px_rgba(179,0,27,0.7)] hover:-translate-y-1 transition-all"
            >
              Order Online
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#menu"
              data-testid="hero-menu-cta"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-brand-cream border-2 border-brand-charcoal text-brand-charcoal font-heading uppercase text-xs tracking-[0.2em] font-bold hover:bg-brand-charcoal hover:text-brand-cream transition-all hover:-translate-y-1"
            >
              View Menu
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-14 flex items-center gap-8 text-brand-charcoal/70"
          >
            <div>
              <div className="font-display text-3xl md:text-4xl text-brand-red">10+</div>
              <div className="font-heading uppercase text-[10px] tracking-[0.2em] font-bold mt-1">Years Serving</div>
            </div>
            <div className="w-px h-12 bg-brand-charcoal/15" />
            <div>
              <div className="font-display text-3xl md:text-4xl text-brand-red">120+</div>
              <div className="font-heading uppercase text-[10px] tracking-[0.2em] font-bold mt-1">Signature Dishes</div>
            </div>
            <div className="w-px h-12 bg-brand-charcoal/15" />
            <div className="hidden sm:block">
              <div className="font-display text-3xl md:text-4xl text-brand-red">4.7★</div>
              <div className="font-heading uppercase text-[10px] tracking-[0.2em] font-bold mt-1">Avg Rating</div>
            </div>
          </motion.div>
        </motion.div>

        {/* Right: dish image */}
        <motion.div
          style={{ y: yDish }}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          className="lg:col-span-5 relative h-[400px] md:h-[560px] lg:h-[640px]"
        >
          {/* Big text behind */}
          <div className="absolute -top-12 -left-12 lg:-left-24 font-display text-[7rem] md:text-[10rem] lg:text-[14rem] leading-none uppercase text-brand-red/10 select-none pointer-events-none">
            Spice
          </div>

          <div className="relative w-full h-full">
            <div className="absolute inset-6 rounded-full bg-brand-mustard/30 blur-2xl" />
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 90, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 rounded-full border-2 border-dashed border-brand-red/20"
            />
            <img
              src={IMAGES.heroBiryani}
              alt="Signature biryani in clay bowl"
              className="absolute inset-4 w-[calc(100%-2rem)] h-[calc(100%-2rem)] object-cover rounded-full shadow-[0_40px_80px_-20px_rgba(179,0,27,0.4)]"
            />
            {/* floating badge */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-2 -left-2 md:-left-6 bg-brand-charcoal text-brand-cream rounded-2xl px-5 py-4 shadow-2xl rotate-[-6deg]"
            >
              <div className="font-display text-3xl leading-none text-brand-mustard">$17.99</div>
              <div className="font-heading uppercase text-[10px] tracking-[0.2em] font-bold mt-1">
                Chicken Biryani
              </div>
            </motion.div>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute -top-2 -right-2 md:-right-6 bg-brand-mustard text-brand-charcoal rounded-full w-24 h-24 md:w-28 md:h-28 flex flex-col items-center justify-center text-center shadow-2xl rotate-[12deg]"
            >
              <div className="font-display text-2xl leading-none uppercase">Fresh</div>
              <div className="font-heading uppercase text-[9px] tracking-[0.15em] font-bold mt-1">
                Daily
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
